// apps/frontend/src/lib/sentry.ts
// Initialisation Sentry frontend — doit etre appelee une seule fois au
// boot, AVANT le render React, pour capturer les erreurs des le chargement.
//
// RGPD : on envoie uniquement user.id (pas d'email ni PII identifiable).
// Le projet Sentry est partage avec le backend ; on les distingue via
// le tag "environment" + le scope auto Sentry React vs Node.

import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const env = import.meta.env.MODE; // 'development' | 'production'

  if (!dsn) {
    // En dev sans DSN configure, on ne plante pas — juste un warning console.
    if (env === 'development') {
      console.info(
        '[Sentry] VITE_SENTRY_DSN non defini — monitoring desactive'
      );
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: env,
    // 20% de traces en prod (transactions perf), 10% en dev.
    tracesSampleRate: env === 'production' ? 0.2 : 0.1,
    // Pas de session replay pour l'instant — surcharge bandwidth + RGPD
    // complique (vise les sessions video des users). A activer plus tard
    // si besoin de comprendre des bugs visuels reportes par les sellers.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // RGPD : on n'envoie jamais l'email ni l'IP du user dans les events.
    sendDefaultPii: false,
    beforeSend(event) {
      // Defense in depth : strip tout PII qui aurait pu se glisser dans
      // user via un setUser malencontreux.
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      // Filtre le bruit connu (ResizeObserver loop, network aborted) qui
      // pollue Sentry sans valeur diagnostique.
      const msg = event.exception?.values?.[0]?.value || event.message || '';
      if (
        msg.includes('ResizeObserver loop') ||
        msg.includes('Non-Error promise rejection captured') ||
        msg.includes('Failed to fetch dynamically imported module')
      ) {
        // Ces erreurs sont gerees ailleurs (lazyWithRetry pour la 3e) ou
        // sans valeur (les 2 premieres). On les drop.
        return null;
      }
      return event;
    },
    // Liste minimale d'integrations — on ajoute des breadcrumbs network +
    // navigation pour avoir le contexte avant le crash, sans surcharger.
    integrations: [Sentry.browserTracingIntegration()],
  });

  initialized = true;
}

/**
 * Attache l'user authentifie au scope Sentry. A appeler apres login/register
 * reussi (setUser dans useAuth) pour que les erreurs suivantes soient
 * taggees avec l'id du user.
 */
export function setSentryUser(
  user: { id: string; role?: string } | null
): void {
  if (!initialized) return;
  if (user) {
    Sentry.setUser({ id: user.id, role: user.role });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture manuelle d'une erreur avec contexte additionnel — utile pour
 * les catch silencieux qu'on veut tracer sans casser le flow user.
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!initialized) return;
  Sentry.withScope(scope => {
    if (context) scope.setExtras(context);
    Sentry.captureException(error);
  });
}

export { Sentry };
